import { useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/design-system/Button'
import { Badge, Card, Skeleton } from '@/design-system/primitives'
import { formatScore, formatShare } from '@/features/competition/score'
import { cn } from '@/lib/cn'

import { useAdminMutation, useAdminQuery } from '../api'
import { UndoPublication } from './UndoPublication'
import type {
  JuryBallot,
  ResultsSent,
  Standing,
  Standings,
  Tiebreak,
  TiebreakBallots,
  TiebreakState,
} from '../types'

/**
 * Le tableau privé d'un concours — tour par tour, puis le cumul.
 *
 * C'est le tableau que quelqu'un lit **debout, au micro, avant d'annoncer un
 * titre**. La règle du client :
 *
 *   note du tour (sur 100) = part des voix du public × 70
 *                          + part des points du jury × 30
 *
 * (70/30 depuis la décision du propriétaire ; les poids arrivent avec le
 * tableau, `public_weight` / `jury_weight`, l'écran ne les recopie pas.)
 *
 * et le titre se joue sur le cumul des trois tours (sur 300). À cumul égal,
 * les points du jury tranchent ; s'ils sont égaux aussi, le départage à cinq
 * QR dans la salle.
 *
 * Rien de ce tableau n'est public. Deux gestes, dans cet ordre, le rendent
 * visible : **proclamer** (la salle apprend un nom), puis **publier les
 * scores** (la salle apprend les chiffres). Chacun demande une confirmation.
 *
 * Et chacun se défait, dans l'ordre inverse, si l'on s'est trompé : retirer
 * les scores, puis annuler la proclamation — chaque fois avec un motif, qui
 * part au journal. Un e-mail de résultats déjà envoyé, lui, ne se rappelle
 * pas : l'encadré le dit avant de confirmer.
 */
export function StandingsPanel({
  eventId,
  competitionCategoryId,
  title,
  can,
  live = false,
}: {
  eventId: string | undefined
  competitionCategoryId: string
  title: string
  /**
   * Les droits du compte connecté. Passés par le parent plutôt que lus dans
   * la session du back-office : le même tableau sert au conducteur du MC
   * (`/ops/deroule`), qui vit hors de `AdminGate`.
   */
  can: (permission: string) => boolean
  /** Relire le tableau toutes les 15 s — au conducteur, pendant que la salle vote. */
  live?: boolean
}) {
  const { t, i18n } = useTranslation()
  // Les boutons suivent les droits : un compte qui ne peut pas proclamer ne
  // voit pas « Proclamer » — il lisait sinon un refus après avoir cliqué.
  const canControl = can('voting.category.control')
  const canReveal = can('competition.result.reveal')
  // Les carrés ne s'affichent qu'à la demande : ce sont des voix, on ne les
  // laisse pas à l'écran d'une tablette posée sur une table.
  const [showBallots, setShowBallots] = useState(false)
  // Retirer des bulletins déjà votés efface des voix : on le demande deux fois.
  const [confirmReissue, setConfirmReissue] = useState(false)
  const [confirm, setConfirm] = useState<'proclaim' | 'publish' | 'send' | null>(null)
  // `null` = le cumul ; sinon l'index du tour affiché.
  const [view, setView] = useState<number | null>(null)

  const path = `/admin/events/${eventId}/competition/categories/${competitionCategoryId}`
  const query = useAdminQuery<Standings>(
    ['admin-standings', competitionCategoryId, i18n.language],
    `${path}/standings?locale=${i18n.language}`,
    eventId,
    { refetchInterval: live ? 15_000 : false },
  )
  const refresh = [
    ['admin-standings', competitionCategoryId],
    ['admin-tiebreak-ballots', competitionCategoryId],
    ['admin-vote-categories', eventId],
    ['admin-candidates', eventId],
    ['public-vote-categories'],
    ['competition-standings'],
  ]

  const openTiebreak = useAdminMutation<
    Tiebreak,
    { candidate_ids: string[]; seats: number; reissue?: boolean }
  >('post', () => `${path}/tiebreak`, eventId, refresh)
  const proclaim = useAdminMutation<Standings, Record<string, never>>(
    'post',
    () => `${path}/proclaim`,
    eventId,
    refresh,
  )
  const publish = useAdminMutation<Standings, Record<string, never>>(
    'post',
    () => `${path}/publish-scores`,
    eventId,
    refresh,
  )
  const unpublish = useAdminMutation<Standings, { reason: string }>(
    'post',
    () => `${path}/unpublish-scores`,
    eventId,
    refresh,
  )
  const cancelProclamation = useAdminMutation<Standings, { reason: string }>(
    'post',
    () => `${path}/cancel-proclamation`,
    eventId,
    refresh,
  )
  const sendResults = useAdminMutation<ResultsSent, Record<string, never>>(
    'post',
    () => `${path}/send-results`,
    eventId,
  )

  const standings = query.data
  const tiebreakOpen = standings?.tiebreak?.status === 'open'
  // Les bulletins du départage EN COURS, relus au serveur : un rechargement
  // de la page ne les fait plus disparaître, et les remontrer ne retire
  // aucune voix. Relus toutes les 15 s pour voir qui a déjà voté.
  const current = useAdminQuery<TiebreakBallots>(
    ['admin-tiebreak-ballots', competitionCategoryId],
    `${path}/tiebreak/ballots`,
    eventId,
    {
      enabled: canControl && tiebreakOpen && showBallots,
      refetchInterval: 15_000,
    },
  )
  const ballots: JuryBallot[] = showBallots && tiebreakOpen ? (current.data?.ballots ?? []) : []
  const score = (value: number) => formatScore(value, i18n.language)
  const share = (value: number) => formatShare(value, i18n.language)
  const nameOf = (id: string | null) =>
    standings?.rows.find((row) => row.candidate_id === id)?.name ?? '—'
  const tiebreak = standings?.tiebreak ?? null
  // Un départage gelé ou publié est clos : aucun bouton ne doit pouvoir
  // effacer ses voix, même après un rechargement de la page. S'il est resté
  // à égalité, on en RELANCE un nouveau à côté (le serveur garde le premier).
  const decided = tiebreak !== null && ['frozen', 'published'].includes(tiebreak.status)
  const relaunch = Boolean(standings?.tiebreak_can_relaunch)
  const tied = standings?.rows.filter((row) => row.tied_after_jury) ?? []

  function launch(reissue: boolean) {
    openTiebreak.mutate(
      { candidate_ids: standings?.tiebreak_candidate_ids ?? [], seats: 5, reissue },
      {
        onSuccess: () => {
          setShowBallots(true)
          setConfirmReissue(false)
        },
      },
    )
  }

  const error =
    proclaim.error ??
    publish.error ??
    unpublish.error ??
    cancelProclamation.error ??
    sendResults.error ??
    openTiebreak.error ??
    current.error

  function previousLine(previous: TiebreakState): string {
    return t('admin.standings.previousTiebreak', {
      round: previous.round,
      cast: previous.ballots_cast,
      seats: previous.seats,
      scores: previous.results.scores.map((entry) => `${entry.name} ${entry.votes}`).join(' · '),
    })
  }

  return (
    <Card className="space-y-5">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h3 className="font-display text-heading-md text-content-primary">{title}</h3>
        {standings ? (
          <span className="flex flex-wrap gap-2">
            <Badge tone={standings.proclaimed ? 'champagne' : 'neutral'}>
              {standings.proclaimed
                ? t('admin.standings.stateProclaimed')
                : t('admin.standings.stateOpen')}
            </Badge>
            <Badge tone={standings.published ? 'success' : 'neutral'}>
              {standings.published
                ? t('admin.standings.statePublished')
                : t('admin.standings.statePrivate')}
            </Badge>
          </span>
        ) : null}
      </div>

      {query.isPending ? <Skeleton className="h-32 w-full" /> : null}
      {query.isError ? (
        <p className="text-sm text-state-danger">{query.error.message}</p>
      ) : null}

      {standings && standings.rows.length === 0 ? (
        <p className="text-sm text-content-secondary">{t('admin.standings.empty')}</p>
      ) : null}

      {standings && standings.jurors.length > 0 ? (
        <p className="text-caption text-content-muted">
          {t('admin.standings.jurors', {
            names: standings.jurors.map((juror) => juror.name).join(' · '),
          })}
        </p>
      ) : standings && standings.rows.length > 0 ? (
        <p className="text-caption text-state-warning">
          {t('admin.standings.noJury', { jury: standings.jury_weight })}
        </p>
      ) : null}

      {standings && standings.rows.length > 0 ? (
        <>
          {/* Le cumul, ou un tour : on passe de l'un à l'autre d'un pouce. */}
          <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1">
            <ViewButton active={view === null} onClick={() => setView(null)}>
              {t('admin.standings.cumulative')}
            </ViewButton>
            {standings.rounds.map((round, index) => (
              <ViewButton key={round || index} active={view === index} onClick={() => setView(index)}>
                {round}
              </ViewButton>
            ))}
          </div>

          {view === null ? (
            <ol className="space-y-3">
              {standings.rows.map((row) => (
                <li
                  key={row.candidate_id}
                  className={cn(
                    'rounded-lg border border-line-subtle px-4 py-3',
                    row.rank === 1 && 'bg-champagne-400/5',
                  )}
                >
                  <div className="flex items-baseline justify-between gap-3">
                    <CandidateLabel row={row} />
                    <span className="shrink-0 font-display tabular-nums text-champagne-300">
                      {score(row.total)}
                      <span className="text-caption text-content-muted"> / 300</span>
                    </span>
                  </div>
                  <p className="mt-1 text-caption text-content-muted">
                    {t('admin.standings.totalsLine', {
                      public: score(row.public_score),
                      jury: score(row.jury_score),
                      votes: row.total_votes,
                      points: row.total_jury_points,
                    })}
                  </p>
                  {row.tied_for_first ? (
                    <p className="mt-1 text-caption text-ember-300">
                      {row.tied_after_jury
                        ? t('admin.standings.tiedAfterJury')
                        : t('admin.standings.tiedJuryDecides')}
                    </p>
                  ) : null}
                </li>
              ))}
            </ol>
          ) : (
            <RoundView standings={standings} index={view} score={score} share={share} />
          )}
        </>
      ) : null}

      {/* Qui l'emporterait si l'on proclamait maintenant — et pourquoi pas encore. */}
      {standings && standings.rows.length > 0 && !standings.proclaimed ? (
        <div className="rounded-lg border border-line-subtle px-5 py-4">
          {standings.leader_candidate_id ? (
            <p className="text-sm text-content-primary">
              {t(`admin.standings.leader.${standings.decided_by ?? 'score'}`, {
                name: nameOf(standings.leader_candidate_id),
              })}
            </p>
          ) : null}
          {standings.proclaim_blocker ? (
            <p className="mt-1 text-caption text-state-warning">
              {t(`admin.standings.blocker.${standings.proclaim_blocker}`)}
            </p>
          ) : null}
        </div>
      ) : null}

      {/* L'égalité que même le jury n'a pas tranchée : le départage à cinq QR. */}
      {standings?.needs_tiebreak && (!decided || relaunch) && !standings.proclaimed && canControl ? (
        <div className="rounded-lg border border-ember-400/40 bg-ember-400/5 px-5 py-4">
          <p className="text-sm text-ember-300">
            {t('admin.standings.tieDetected', {
              names: tied.map((row) => row.name).join(' · '),
            })}
          </p>
          <p className="mt-1 text-caption text-content-muted">
            {relaunch ? t('admin.standings.relaunchHelp') : t('admin.standings.tieHelp')}
          </p>
          {confirmReissue ? (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <p className="text-caption text-state-warning">
                {t('admin.standings.reissueConfirm', { count: tiebreak?.ballots_cast ?? 0 })}
              </p>
              <Button
                size="sm"
                variant="ember"
                disabled={openTiebreak.isPending}
                onClick={() => launch(true)}
              >
                {t('admin.standings.reissueYes')}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setConfirmReissue(false)}>
                {t('admin.cancel')}
              </Button>
            </div>
          ) : (
            <Button
              className="mt-3 h-auto min-h-10 whitespace-normal py-2 text-center"
              size="sm"
              disabled={openTiebreak.isPending}
              onClick={() =>
                // Relancer n'efface rien (un nouveau départage s'ouvre à côté) :
                // pas de confirmation. Retirer des bulletins déjà votés, si.
                !relaunch && tiebreak && tiebreak.ballots_cast > 0
                  ? setConfirmReissue(true)
                  : launch(false)
              }
            >
              {relaunch
                ? t('admin.standings.relaunchTiebreak')
                : tiebreak
                  ? t('admin.standings.reissueBallots')
                  : t('admin.standings.openTiebreak')}
            </Button>
          )}
          {/* Remontrer les carrés sans rien retirer : la page a été
              rechargée, un juré n'a pas encore scanné le sien. */}
          {tiebreakOpen && !showBallots ? (
            <div className="mt-3">
              <Button size="sm" variant="outline" onClick={() => setShowBallots(true)}>
                {t('admin.standings.showBallots')}
              </Button>
              <p className="mt-1 text-caption text-content-muted">
                {t('admin.standings.showBallotsHelp')}
              </p>
            </div>
          ) : null}
        </div>
      ) : null}

      {/* Les cinq carrés, à montrer aux cinq personnes désignées. Relus au
          serveur : les mêmes qu'à l'ouverture, toujours valables. Seul
          le retrait explicite des voix en tire d'autres. */}
      {ballots.length > 0 ? (
        <div>
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <p className="overline text-content-muted">{t('admin.standings.ballots')}</p>
            <Button size="sm" variant="ghost" onClick={() => setShowBallots(false)}>
              {t('admin.standings.hideBallots')}
            </Button>
          </div>
          <p className="mt-1 text-caption text-content-muted">
            {t('admin.standings.ballotsHelp')}
          </p>
          <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-5">
            {ballots.map((ballot) => (
              <div key={ballot.attendee_id} className="text-center">
                <div className={cn('rounded-md bg-white p-2', ballot.voted && 'opacity-30')}>
                  <img src={ballot.svg} alt="" className="h-full w-full" />
                </div>
                <p className="mt-2 text-caption text-content-secondary">
                  {t('admin.standings.seat', { seat: ballot.seat })}
                  {ballot.voted ? ` · ${t('admin.standings.seatVoted')}` : ''}
                </p>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {/* L'historique : les départages restés à égalité, puis relancés. Leurs
          voix restent — on les montre, on n'y touche pas. */}
      {standings && standings.previous_tiebreaks.length > 0 ? (
        <ul className="space-y-1 text-caption text-content-muted">
          {standings.previous_tiebreaks.map((previous) => (
            <li key={previous.vote_category_id}>{previousLine(previous)}</li>
          ))}
        </ul>
      ) : null}

      {tiebreak ? (
        <div className="rounded-lg border border-line-subtle px-5 py-4">
          <div className="flex flex-wrap items-center gap-2">
            {tiebreak.round > 1 ? (
              <span className="text-caption text-content-secondary">
                {t('admin.standings.tiebreakRound', { round: tiebreak.round })}
              </span>
            ) : null}
            <Badge tone={decided ? 'neutral' : 'ember'}>
              {t(`admin.standings.tiebreakStatus.${tiebreak.status}`)}
            </Badge>
            <span className="text-caption text-content-muted">
              {t('admin.standings.tiebreakBallots', {
                cast: tiebreak.ballots_cast,
                seats: tiebreak.seats,
              })}
            </span>
          </div>
          {tiebreak.results.scores.length > 0 ? (
            <ul className="mt-3 space-y-1 text-sm">
              {tiebreak.results.scores.map((entry) => (
                <li key={entry.candidate_id ?? entry.name} className="flex justify-between gap-3">
                  <span className="text-content-primary">{entry.name}</span>
                  <span className="tabular-nums text-champagne-300">{entry.votes}</span>
                </li>
              ))}
            </ul>
          ) : null}
          <p className="mt-3 text-sm text-content-secondary">
            {tiebreak.leader_candidate_id
              ? t('admin.standings.tiebreakLeader', { name: nameOf(tiebreak.leader_candidate_id) })
              : t('admin.standings.tiebreakUndecided')}
          </p>
        </div>
      ) : null}

      {/* Les deux gestes de la fin, puis l'envoi aux candidats — réservés à
          qui peut publier. Les autres lisent le tableau, sans bouton. */}
      {standings && standings.rows.length > 0 && !canReveal && standings.proclaimed ? (
        <p className="font-display text-heading-sm text-champagne-300">
          {t('admin.standings.titleWon', { name: nameOf(standings.winner_candidate_id) })}
        </p>
      ) : null}
      {standings && standings.rows.length > 0 && canReveal ? (
        <div className="space-y-3 rounded-lg border border-line-subtle px-5 py-4">
          {standings.proclaimed ? (
            <p className="font-display text-heading-sm text-champagne-300">
              {t('admin.standings.titleWon', { name: nameOf(standings.winner_candidate_id) })}
            </p>
          ) : null}

          {!standings.proclaimed ? (
            confirm === 'proclaim' ? (
              <Confirm
                warning={t('admin.standings.proclaimWarning', {
                  name: nameOf(standings.leader_candidate_id),
                })}
                label={t('admin.standings.proclaimConfirm')}
                busy={proclaim.isPending}
                onConfirm={() => proclaim.mutate({}, { onSettled: () => setConfirm(null) })}
                onCancel={() => setConfirm(null)}
              />
            ) : (
              <Button
                size="sm"
                variant="champagne"
                disabled={Boolean(standings.proclaim_blocker) || proclaim.isPending}
                onClick={() => setConfirm('proclaim')}
              >
                {t('admin.standings.proclaim')}
              </Button>
            )
          ) : !standings.published ? (
            confirm === 'publish' ? (
              <Confirm
                warning={t('admin.standings.publishWarning')}
                label={t('admin.standings.publishConfirm')}
                busy={publish.isPending}
                onConfirm={() => publish.mutate({}, { onSettled: () => setConfirm(null) })}
                onCancel={() => setConfirm(null)}
              />
            ) : (
              <>
                <p className="text-caption text-content-muted">
                  {t('admin.standings.publishHelp')}
                </p>
                <div className="flex flex-wrap items-start gap-2">
                  <Button size="sm" variant="ember" onClick={() => setConfirm('publish')}>
                    {t('admin.standings.publish')}
                  </Button>
                  {/* Le mauvais nom au micro : on libère le titre. Possible
                      seulement tant que les scores ne sont pas publiés. */}
                  <UndoPublication
                    label={t('admin.undo.proclaimCancel')}
                    confirmLabel={t('admin.undo.proclaimCancelConfirm')}
                    warnings={[
                      t('admin.undo.proclaimCancelWarning', {
                        name: nameOf(standings.winner_candidate_id),
                      }),
                      t('admin.undo.noMail'),
                    ]}
                    busy={cancelProclamation.isPending}
                    onConfirm={(reason) => cancelProclamation.mutateAsync({ reason })}
                  />
                </div>
              </>
            )
          ) : (
            <>
              <p className="text-caption text-state-success">{t('admin.standings.publicNow')}</p>
              <UndoPublication
                label={t('admin.undo.scoresUnpublish')}
                confirmLabel={t('admin.undo.scoresUnpublishConfirm')}
                warnings={[
                  // Le plus grave d'abord : ce qui est parti ne revient pas.
                  ...(standings.results_sent_at
                    ? [
                        t('admin.undo.scoresUnpublishMailed', {
                          date: new Date(standings.results_sent_at).toLocaleString(i18n.language),
                        }),
                      ]
                    : []),
                  t('admin.undo.scoresUnpublishWarning'),
                  t('admin.undo.noMail'),
                ]}
                busy={unpublish.isPending}
                onConfirm={(reason) => unpublish.mutateAsync({ reason })}
              />
              <p className="text-caption text-content-muted">
                {t('admin.undo.proclaimCancelLocked')}
              </p>
            </>
          )}

          {/* Chaque candidat reçoit SES résultats — après la publication. */}
          <div className="border-t border-line-subtle pt-3">
            <p className="text-caption text-content-muted">
              {standings.published
                ? t('admin.standings.sendResultsHelp')
                : t('admin.standings.sendResultsLocked')}
            </p>
            {confirm === 'send' ? (
              <Confirm
                label={t('admin.standings.sendResultsConfirm', { count: standings.rows.length })}
                busy={sendResults.isPending}
                onConfirm={() => sendResults.mutate({}, { onSettled: () => setConfirm(null) })}
                onCancel={() => setConfirm(null)}
              />
            ) : (
              <Button
                // Libellé long (« Ergebnisse an die Kandidat·innen senden ») :
                // il passe à la ligne plutôt que de sortir de l'écran.
                className="mt-3 h-auto min-h-10 whitespace-normal py-2 text-center"
                size="sm"
                variant="outline"
                disabled={!standings.published || sendResults.isPending}
                onClick={() => setConfirm('send')}
              >
                {t('admin.standings.sendResults')}
              </Button>
            )}
            {sendResults.isSuccess ? (
              <p className="mt-2 text-caption text-state-success">
                {t('admin.standings.resultsSent', { count: sendResults.data.sent })}
                {sendResults.data.failed > 0
                  ? ` · ${t('admin.standings.resultsFailed', { count: sendResults.data.failed })}`
                  : ''}
              </p>
            ) : null}
          </div>
        </div>
      ) : null}

      {error ? <p className="text-caption text-state-danger">{error.message}</p> : null}
    </Card>
  )
}

function ViewButton({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'min-h-10 shrink-0 rounded-full border px-4 py-2 text-sm transition',
        active
          ? 'border-champagne-400/60 bg-bg-overlay text-content-primary'
          : 'border-line-subtle text-content-secondary hover:text-content-primary',
      )}
    >
      {children}
    </button>
  )
}

function CandidateLabel({ row }: { row: Standing }) {
  return (
    <span className="flex min-w-0 items-baseline gap-2">
      <span className="w-5 shrink-0 font-display tabular-nums text-content-secondary">
        {row.rank}
      </span>
      {row.candidate_number !== null ? (
        <span className="font-mono text-caption text-content-muted">#{row.candidate_number}</span>
      ) : null}
      <span className="truncate text-content-primary">{row.name}</span>
    </span>
  )
}

/** Un tour : où en sont la salle et le jury, puis chaque candidat, détaillé. */
function RoundView({
  standings,
  index,
  score,
  share,
}: {
  standings: Standings
  index: number
  score: (value: number) => string
  share: (value: number) => string
}) {
  const { t } = useTranslation()
  const detail = standings.round_details[index]
  const rows = standings.rows
    .filter((row) => row.per_round[index]?.competed)
    .sort((a, b) => (b.per_round[index]?.score ?? 0) - (a.per_round[index]?.score ?? 0))

  return (
    <div className="space-y-3">
      {detail ? (
        <p
          className={cn(
            'text-caption',
            detail.jury_marks_given < detail.jury_marks_expected
              ? 'text-state-warning'
              : 'text-content-muted',
          )}
        >
          {t('admin.standings.roundLine', {
            status: t(`admin.voting.status.${detail.status}`),
            votes: detail.public_votes,
            given: detail.jury_marks_given,
            expected: detail.jury_marks_expected,
          })}
        </p>
      ) : null}
      <ul className="space-y-3">
        {rows.map((row) => {
          const round = row.per_round[index]!
          return (
            <li key={row.candidate_id} className="rounded-lg border border-line-subtle px-4 py-3">
              <div className="flex items-baseline justify-between gap-3">
                <span className="min-w-0 truncate text-content-primary">
                  {row.candidate_number !== null ? (
                    <span className="mr-2 font-mono text-caption text-content-muted">
                      #{row.candidate_number}
                    </span>
                  ) : null}
                  {row.name}
                </span>
                <span className="shrink-0 font-display tabular-nums text-champagne-300">
                  {score(round.score)}
                  <span className="text-caption text-content-muted"> / 100</span>
                </span>
              </div>
              <dl className="mt-2 grid grid-cols-2 gap-3 text-caption">
                <div>
                  <dt className="text-content-muted">{t('admin.standings.public')}</dt>
                  <dd className="text-content-secondary">
                    {t('admin.standings.publicDetail', {
                      count: round.votes,
                      share: share(round.public_share),
                      points: score(round.public_score),
                    })}
                  </dd>
                </div>
                <div>
                  <dt className="text-content-muted">{t('admin.standings.jury')}</dt>
                  <dd className="text-content-secondary">
                    {t('admin.standings.juryDetail', {
                      points: round.jury_points,
                      share: share(round.jury_share),
                      score: score(round.jury_score),
                    })}
                  </dd>
                </div>
              </dl>
              {round.jury_marks.length > 0 ? (
                <ul className="mt-2 flex flex-wrap gap-2">
                  {round.jury_marks.map((mark) => (
                    <li
                      key={mark.juror_id}
                      className={cn(
                        'rounded-full border px-2 py-0.5 text-caption',
                        mark.score === null
                          ? 'border-state-warning/40 text-state-warning'
                          : 'border-line-subtle text-content-secondary',
                      )}
                    >
                      {mark.juror_name} · {mark.score ?? '—'}
                    </li>
                  ))}
                </ul>
              ) : null}
            </li>
          )
        })}
      </ul>
    </div>
  )
}

function Confirm({
  warning,
  label,
  busy,
  onConfirm,
  onCancel,
}: {
  warning?: string
  label: string
  busy: boolean
  onConfirm: () => void
  onCancel: () => void
}) {
  const { t } = useTranslation()
  return (
    <div className="mt-3 space-y-2">
      {warning ? <p className="text-caption text-state-warning">{warning}</p> : null}
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="ember" disabled={busy} onClick={onConfirm}>
          {label}
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel}>
          {t('admin.cancel')}
        </Button>
      </div>
    </div>
  )
}
